import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Send } from "lucide-react";
import { BASE_URL } from "../../config/api";
import toast from "react-hot-toast";

export default function RemarkChat({ entityType, entityId }) {
  const authUser = JSON.parse(localStorage.getItem("authUserData"));
  const userId = authUser?.id;
  const firstName = authUser?.first_name || "";
  const lastName = authUser?.last_name || "";

  const username = `${firstName} ${lastName}`.trim();

  const [remarks, setRemarks] = useState([]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  /* ---------------- FETCH REMARKS ---------------- */
 
    const fetchRemarks = async () => {
    try {
        const res = await axios.get(
        `${BASE_URL}warranty/retailer-connection?retailer_id=${entityId}`
        );

        const cleaned = (res.data.data || [])
        .filter((r) => r.note && r.note.trim() !== "")
        .reverse(); // 👈 latest goes to bottom

        setRemarks((prev) => {
        const prevLastId = prev[prev.length - 1]?.id;
        const newLastId = cleaned[cleaned.length - 1]?.id;
        return prevLastId === newLastId ? prev : cleaned;
        });
    } catch (err) {
        console.error(err);
    }
    };



    useEffect(() => {
        fetchRemarks(); // initial load

        const interval = setInterval(() => {
            fetchRemarks(); // auto refresh
        }, 5000); // ⏱️ every 5 seconds

        return () => clearInterval(interval);
        }, [entityId]);


  /* ---------------- AUTO SCROLL ---------------- */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [remarks]);

  /* ---------------- SEND REMARK ---------------- */
  const handleSend = async () => {
    if (!message.trim()) return;

    const tempRemark = {
      id: Date.now(),
      note: message,
      created_by_id: userId,
      created_by_name: username || "You",
      created_at: new Date().toISOString(),
      optimistic: true,
    };

    // Optimistic UI
    setRemarks((prev) => [...prev, tempRemark]);
    setMessage("");

    try {
  setSending(true);

  const res = await axios.post(
    `${BASE_URL}warranty/retailer-connection`,
    {
      created_by_id: userId,
      created_by_name: username,
      retailer_id: entityId,
      note: message,
    }
  );

  if (res.data?.status) {
    // replace optimistic message with real one
    setRemarks((prev) =>
      prev.map((r) =>
        r.id === tempRemark.id ? res.data.data : r
      )
    );
  } else {
    throw new Error("API returned false status");
  }
    } catch (err) {
    toast.error("Failed to send remark");

    setRemarks((prev) =>
        prev.filter((r) => r.id !== tempRemark.id)
    );
    } finally {
    setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-[70vh] border border-blue-100 rounded-2xl bg-white shadow-sm">
      {/* CHAT BODY */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {remarks.length === 0 && (
          <p className="text-sm text-center text-slate-500">
            No remarks yet
          </p>
        )}

        {remarks.map((r) => {
          const isMine = r.created_by_id === userId;

          return (
            <div
              key={r.id}
              className={`flex ${isMine ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[70%] rounded-2xl px-4 py-2 text-sm font-semibold
                  ${
                    isMine
                      ? "bg-blue-600 text-white rounded-br-none"
                      : "bg-slate-100 text-slate-800 rounded-bl-none"
                  }
                `}
              >
                {!isMine && (
                  <p className="mb-1 text-[11px] font-bold text-slate-500">
                    {r.created_by_name}
                  </p>
                )}

                {r.note}

                <p className="mt-1 text-[10px] text-right opacity-70">
                  {new Date(r.created_at).toLocaleTimeString()}
                </p>
              </div>
            </div>
          );
        })}

        <div ref={bottomRef} />
      </div>

      {/* INPUT */}
      <div className="border-t border-blue-100 p-3 flex gap-2">
        <input
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Enter remark..."
          className="flex-1 rounded-xl border border-slate-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
        />

        <button
          disabled={sending}
          onClick={handleSend}
          className="rounded-xl bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

